import React, { Fragment, useContext, useEffect, useMemo } from 'react'

import {
  BackdropLoader,
  BackdropProps,
  Box,
  Divider,
  ProviderLogo,
  Typography,
  useAppTheme,
} from '@percept/mui'

import { DimensionalSummary } from 'components/DimensionalSummary'

import { PerformanceControls } from 'components/PerformanceControls'

import { PerformanceTrackers } from './PerformanceTrackers'

import { cellRenderers } from './channelTable'

import { usePlatformUnit, useOrgUnitCategoryScores, useChildOrgUnitCategoryScores, useNavigation } from '@percept/hooks'

import {
  useNestedDoubleVerifyProviderComparisons,
  useNestedDoubleVerifyProviderTimeseries,
  useNestedProviderComparisons,
  useNestedProviderTimeseries,
} from 'hooks'

import { isStructuralReportProvider, usePerformanceDatasets, usePlatformUnitDatasets } from './lib'

import { get, some } from 'lodash-es'

import { coerceReportProvider, isLoading, isoDateUTC } from '@percept/utils'

import { format, isBefore } from 'date-fns'

import { channelDisplayMap, providerDoubleVerifyMap, providerURLMap, urlProviderMap } from '@percept/constants'

import { PerformanceReportingDatum } from 'types'

import { PerformanceTableProps } from 'components/Tables'

import {
  AnyPerformanceDimension,
  ChannelKey,
  PerformanceReportingDimension,
  PlatformUnitContainer,
  PlatformUnitProviderInfo,
  PrimaryPerformanceDataProvider,
  TimeseriesGranularity,
} from '@percept/types'

import { platformUnitHasDoubleVerify, resolvePerformanceReportingDimensions } from 'components/Organisation/lib'

import { DashboardContext } from 'contexts'
import { OrgUnitCategoryScoreGroup } from 'components/CategoryScores'


const performanceTableProps: Partial<
  PerformanceTableProps<PerformanceReportingDatum & { dimension: PerformanceReportingDimension }>
> = {
  renderers: cellRenderers,
}


export type MultiUnitChannelProps = {
  platformUnit: PlatformUnitContainer
  channel: ChannelKey | null
  provider?: PrimaryPerformanceDataProvider | null
  referenceDate: Date | null
  providerInfo: PlatformUnitProviderInfo
}


export const MultiUnitChannel = ({
  platformUnit,
  channel,
  provider = null,
  providerInfo,
  referenceDate,
}: MultiUnitChannelProps): JSX.Element => {

  const [state, setState] = useContext(DashboardContext)

  const { currency, dimension } = state
  const activeDimension = dimension as AnyPerformanceDimension

  const referenceDateParam = referenceDate && isoDateUTC(referenceDate)
  
  const org_unit_id = platformUnit.id

  const org_unit_ids = useMemo(() => {
    if( platformUnit.children && platformUnit.children.length ){
      return platformUnit.children.map( ({ id }) => id )
    }else{
      return [platformUnit.id]
    }
  }, [platformUnit])

  const [platformUnitDetail] = usePlatformUnit({
    org_unit_id,
  })

  const hasDoubleVerify = !!(
    platformUnitDetail.data && platformUnitHasDoubleVerify(platformUnitDetail.data, provider)
  )

  const providerReferenceDate = provider && get(
    providerInfo, [provider, 'reference_date'], null
  )

  const doubleVerifyProvider = provider && providerDoubleVerifyMap[provider]

  const reportedDoubleVerifyReferenceDate = doubleVerifyProvider && get(
    providerInfo, [doubleVerifyProvider, 'reference_date'], null
  )

  const doubleVerifyReferenceDate = (
    reportedDoubleVerifyReferenceDate
    && referenceDate
    && (
      reportedDoubleVerifyReferenceDate.getTime() > referenceDate.getTime() ?
        referenceDate :
        reportedDoubleVerifyReferenceDate
    )
  )

  const requiresDoubleVerifyTrigger = !!(
    hasDoubleVerify &&
    doubleVerifyProvider &&
    referenceDate &&
    doubleVerifyReferenceDate &&
    isBefore(doubleVerifyReferenceDate, referenceDate)
  )

  const showDoubleVerify = !!(
    hasDoubleVerify &&
    doubleVerifyProvider &&
    !requiresDoubleVerifyTrigger
  )

  const availableDimensions = useMemo(() => {
    if( platformUnitDetail.data ){
      return resolvePerformanceReportingDimensions({
        platformUnit: platformUnitDetail.data,
        provider,
        includeDoubleVerify: showDoubleVerify,
      })
    }
    return undefined
  }, [provider, platformUnitDetail.data, showDoubleVerify])
  
  useEffect(() => {
    if(
      availableDimensions
      && !availableDimensions.includes(activeDimension as AnyPerformanceDimension) ){
      setState({ dimension: availableDimensions[0] })
    }
  }, [availableDimensions, activeDimension, setState])

  const timeseriesByUnit = useNestedProviderTimeseries({
    org_unit_ids,
    provider,
    chunking: 'MONTH',
    period: 'DAYS_365',
    target_currency: currency,
    ...(referenceDateParam && {
      reference_date: referenceDateParam
    })
  })

  const doubleVerifyTimeseriesByUnit = useNestedDoubleVerifyProviderTimeseries({
    org_unit_ids,
    provider,
    chunking: 'MONTH',
    period: 'DAYS_365',
    enabled: showDoubleVerify,
    ...(referenceDateParam && {
      reference_date: referenceDateParam
    })
  })

  const comparisonsByUnit = useNestedProviderComparisons({
    org_unit_ids,
    provider,
    target_currency: currency,
    ...(referenceDateParam && {
      reference_date: referenceDateParam
    })
  })

  const doubleVerifyComparisonsByUnit = useNestedDoubleVerifyProviderComparisons({
    org_unit_ids,
    provider,
    enabled: showDoubleVerify,
    ...(referenceDateParam && {
      reference_date: referenceDateParam
    })
  })

  const categoryScoreGranularity: TimeseriesGranularity = 'WEEK'

  const aggregatedCategoryScores = useOrgUnitCategoryScores({
    org_unit_id,
    granularity: categoryScoreGranularity,
  })

  const categoryScoresByOrgUnit = useChildOrgUnitCategoryScores({
    org_unit_id,
    granularity: categoryScoreGranularity,
  })

  const {
    timeseriesCategoryScoreMapping,
    childOrgUnitCategoryScores,
  } = useMemo(() => {
    const timeseriesCategoryScoreMapping = (aggregatedCategoryScores.data || []).find(
      d => coerceReportProvider(d.provider) === provider
    )
    const childOrgUnitCategoryScores = (categoryScoresByOrgUnit.data || []).filter(
      d => coerceReportProvider(d.provider) === provider
    )
    return {
      timeseriesCategoryScoreMapping: get(timeseriesCategoryScoreMapping, 'timeseriesByCategory', null),
      childOrgUnitCategoryScores,
    }
  }, [provider, aggregatedCategoryScores.data, categoryScoresByOrgUnit.data])

  const channelLabel: React.ReactNode | null = (
    provider && <ProviderLogo provider={provider} size={1.25} /> ||
    channel && channelDisplayMap[channel].label
  )

  const appTheme = useAppTheme()

  const navigate = useNavigation()

  const platformUnitDatasets = usePlatformUnitDatasets({
    platformUnit,
    timeseriesByUnit: timeseriesByUnit.data,
    doubleVerifyTimeseriesByUnit: doubleVerifyTimeseriesByUnit.data,
    comparisonsByUnit: comparisonsByUnit.data,
    doubleVerifyComparisonsByUnit: doubleVerifyComparisonsByUnit.data,
    currency,
    appTheme,
    channel,
  })

  const dimensionalDatasets = usePerformanceDatasets({
    datasets: platformUnitDatasets,
    appTheme,
    dimension: dimension === 'potential_efficiency' ? 'cost' : activeDimension,
    currency,
  })

  const showContent = Boolean(
    !comparisonsByUnit.loading &&
    !timeseriesByUnit.loading &&
    !categoryScoresByOrgUnit.isLoading &&
    !aggregatedCategoryScores.isLoading && (
      showDoubleVerify ? (
        !doubleVerifyTimeseriesByUnit.loading &&
        !doubleVerifyComparisonsByUnit.loading
      ) : true
    )
  )

  const backdropProps: Partial<BackdropProps> = {
    open: (
      categoryScoresByOrgUnit.isLoading
      || aggregatedCategoryScores.isLoading
      || some([comparisonsByUnit, timeseriesByUnit], isLoading)
      || (
        showDoubleVerify && some([doubleVerifyComparisonsByUnit, doubleVerifyTimeseriesByUnit], isLoading)
      )
    )
  }

  return (
    <Box
      my={4}
      mx={3}>

      { (
        showContent
        && availableDimensions
        && (
          dimension === 'potential_efficiency'
          || availableDimensions.includes(activeDimension)
        )
      ) && (
        <Fragment>

          { provider && isStructuralReportProvider(provider) && (
            <>
              <Box mt={5} mb={3}>
                <Typography variant='h5'>
                  Quality Analysis
                </Typography>
              </Box>

              { timeseriesCategoryScoreMapping && (
                <Box mb={3}>
                  <OrgUnitCategoryScoreGroup
                    key={org_unit_id}
                    granularity={categoryScoreGranularity}
                    aggregatedCategoryScores={timeseriesCategoryScoreMapping}
                    orgUnitProviderCategoryScores={childOrgUnitCategoryScores}
                    onItemClick={(dataset): void => {
                      const matchedOrg = (platformUnit.children || []).find( c => (
                        c.id === dataset.org_unit_id
                      ))
                      if( matchedOrg ){
                        const matchedSeries = (matchedOrg.report_series || []).find( s => (
                          coerceReportProvider(s.provider.slug) === provider
                        ))
                        if( matchedSeries ){
                          navigate(
                            `/series/${matchedSeries.id}`,
                            {
                              search: {
                                category: dataset.category,
                              },
                            }
                          )
                        }else{
                          navigate(
                            `/dashboards/${matchedOrg.id}/${providerURLMap[provider]}`
                          )
                        }
                      }
                    }} />
                </Box>
              )}

              <Box my={3}>
                <Divider />
              </Box>
            </>
          )}

          <PerformanceControls
            platformUnit={platformUnit}
            availableDimensions={availableDimensions}
            referenceDate={referenceDate}
            maxDate={providerReferenceDate}
            doubleVerifyReferenceDate={doubleVerifyReferenceDate}
            potentialEfficiencyEnabled={false}
            requiresDoubleVerifyTrigger={requiresDoubleVerifyTrigger} />

          <Box mt={5}>
            <DimensionalSummary
              dimension={activeDimension}
              referenceDate={referenceDate}
              color={channel || 'primary'}
              datasets={dimensionalDatasets}
              currency={currency}
              PerformanceTableProps={performanceTableProps} />
          </Box>

          <Box mt={4}>
            <PerformanceTrackers
              currency={currency}
              availableDimensions={availableDimensions}
              referenceDate={referenceDate}
              datasets={platformUnitDatasets} />
          </Box>

        </Fragment>
      )}

      <BackdropLoader
        BackdropProps={backdropProps} />

    </Box>
  )
}
